/**
 * Operator-facing identity for Sync & Events.
 *
 * Resolves product/SKU/channel labels from local canonical rows only.
 * Does not call marketplace providers and does not mutate jobs.
 */

import type { PriceStrategy } from "@/lib/channels";
import { computeChannelPrice, pricingConfigFromProviderConfig } from "@/lib/listing-pricing";
import { providerMeta } from "@/lib/integrations/registry";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SENSITIVE_KEY = /secret|token|password|credential|authorization|refresh_token|access_key|private_key/i;

export function asUuid(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const s = value.trim();
  return UUID_RE.test(s) ? s : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s ? s : null;
}

function finiteNumber(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

export function optionValuesLabel(optionValues: unknown): string | null {
  const rec = asRecord(optionValues);
  if (!rec) return null;
  const parts = Object.values(rec)
    .map((v) => text(v))
    .filter((v): v is string => Boolean(v));
  return parts.length ? parts.join(" / ") : null;
}

export interface ConnectorIdentity {
  id: string;
  name: string;
  provider: string | null;
}

export interface ProductIdentity {
  id: string;
  name: string | null;
}

export interface VariantIdentity {
  id: string;
  product_id: string | null;
  sku: string | null;
  option_values: Record<string, string> | null;
  selling_price: number | null;
}

export interface ListingIdentity {
  id: string;
  connector_id: string | null;
  product_id: string | null;
  price_strategy: PriceStrategy | string | null;
  provider_config: Record<string, unknown> | null;
  included_variant_count: number | null;
}

export interface ListingVariantPrice {
  listing_id: string;
  variant_id: string;
  included: boolean;
  effective_channel_price: number | null;
  selling_price: number | null;
}

export interface OrderIdentity {
  id: string;
  order_number: string | null;
  product_names: string[];
}

export interface SyncEventsCatalog {
  products: Record<string, ProductIdentity>;
  variants: Record<string, VariantIdentity>;
  listings: Record<string, ListingIdentity>;
  listingVariantPrices: ListingVariantPrice[];
  connectors: Record<string, ConnectorIdentity>;
  orders: Record<string, OrderIdentity>;
  sellableByListingVariant: Record<string, number>;
}

export function emptySyncEventsCatalog(): SyncEventsCatalog {
  return {
    products: {},
    variants: {},
    listings: {},
    listingVariantPrices: [],
    connectors: {},
    orders: {},
    sellableByListingVariant: {},
  };
}

export function sellableLookupKey(listingId: string, variantId: string): string {
  return `${listingId}:${variantId}`;
}

export function channelDisplayLine(connector: ConnectorIdentity | null | undefined): string {
  if (!connector) return "Unknown channel";
  const providerLabel = providerMeta(connector.provider)?.label ?? text(connector.provider);
  const name = text(connector.name) ?? "Unnamed connector";
  if (providerLabel && !name.toLowerCase().includes(providerLabel.toLowerCase())) {
    return `${providerLabel} · ${name}`;
  }
  return name;
}

export function jobTypeDisplay(jobType: string, domains: string[]): string {
  const unique = [...new Set(domains.map((d) => d.trim()).filter(Boolean))];
  if (jobType === "inventory_publish") return "Inventory publish";
  if (jobType === "price_publish") return "Price publish";
  if (jobType === "product_sync") return "Product sync";
  if (jobType === "order_import") return "Order import";
  if (jobType === "listing_domain_sync") {
    if (unique.length === 1 && unique[0] === "variants") return "Variants sync";
    if (unique.length === 1 && unique[0] === "pricing") return "Price publish";
    if (unique.length === 1 && unique[0] === "inventory") return "Inventory publish";
    if (unique.length) return unique.map((d) => d.charAt(0).toUpperCase() + d.slice(1)).join(" + ") + " sync";
    return "Listing sync";
  }
  return jobType.replace(/_/g, " ");
}

export interface JobEntityRefs {
  productId: string | null;
  variantId: string | null;
  listingId: string | null;
  ledgerId: string | null;
  salesOrderId: string | null;
  domains: string[];
  provider: string | null;
}

export function extractJobRefs(job: {
  payload?: Record<string, unknown> | null;
  entity_type?: string | null;
  entity_id?: string | null;
  connector_id?: string | null;
}): JobEntityRefs {
  const payload = asRecord(job.payload) ?? {};
  const domainsRaw = payload.domains;
  const domains = Array.isArray(domainsRaw)
    ? domainsRaw.map((d) => text(d)).filter((d): d is string => Boolean(d))
    : text(domainsRaw)
      ? [text(domainsRaw)!]
      : [];
  let productId = asUuid(payload.product_id);
  let variantId = asUuid(payload.variant_id);
  let listingId = asUuid(payload.listing_id);
  const ledgerId = asUuid(payload.ledger_id);
  const salesOrderId = asUuid(payload.sales_order_id) ?? asUuid(payload.so_id);
  if (!listingId && job.entity_type === "channel_listing") listingId = asUuid(job.entity_id);
  if (!productId && job.entity_type === "product") productId = asUuid(job.entity_id);
  if (!variantId && job.entity_type === "variant") variantId = asUuid(job.entity_id);
  return {
    productId,
    variantId,
    listingId,
    ledgerId,
    salesOrderId,
    domains,
    provider: text(payload.provider),
  };
}

export function extractEventRefs(event: {
  aggregate_type?: string | null;
  aggregate_id?: string | null;
  payload?: Record<string, unknown> | null;
}): { productId: string | null; variantId: string | null; salesOrderId: string | null; connectorId: string | null } {
  const payload = asRecord(event.payload) ?? {};
  let productId = asUuid(payload.product_id);
  let variantId = asUuid(payload.variant_id);
  let salesOrderId = asUuid(payload.sales_order_id) ?? asUuid(payload.so_id);
  const connectorId = asUuid(payload.connector_id);
  const aggregateId = asUuid(event.aggregate_id);
  if (!productId && event.aggregate_type === "product") productId = aggregateId;
  if (!salesOrderId && (event.aggregate_type === "sales_order" || event.aggregate_type === "sales_refund")) {
    salesOrderId = salesOrderId ?? (event.aggregate_type === "sales_order" ? aggregateId : asUuid(payload.sales_order_id));
  }
  return { productId, variantId, salesOrderId, connectorId };
}

function uniqueIds(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))];
}

export function collectCatalogIds(
  jobs: Array<{ payload?: Record<string, unknown> | null; entity_type?: string | null; entity_id?: string | null; connector_id?: string | null }>,
  events: Array<{ aggregate_type?: string | null; aggregate_id?: string | null; payload?: Record<string, unknown> | null }>,
): { productIds: string[]; variantIds: string[]; listingIds: string[]; orderIds: string[]; connectorIds: string[] } {
  const productIds: string[] = [];
  const variantIds: string[] = [];
  const listingIds: string[] = [];
  const orderIds: string[] = [];
  const connectorIds: string[] = [];
  for (const job of jobs) {
    const refs = extractJobRefs(job);
    productIds.push(refs.productId ?? "");
    variantIds.push(refs.variantId ?? "");
    listingIds.push(refs.listingId ?? "");
    orderIds.push(refs.salesOrderId ?? "");
    if (job.connector_id) connectorIds.push(job.connector_id);
  }
  for (const event of events) {
    const refs = extractEventRefs(event);
    productIds.push(refs.productId ?? "");
    variantIds.push(refs.variantId ?? "");
    orderIds.push(refs.salesOrderId ?? "");
    if (refs.connectorId) connectorIds.push(refs.connectorId);
  }
  return {
    productIds: uniqueIds(productIds),
    variantIds: uniqueIds(variantIds),
    listingIds: uniqueIds(listingIds),
    orderIds: uniqueIds(orderIds),
    connectorIds: uniqueIds(connectorIds),
  };
}

export function intendedChannelPrice(opts: {
  strategy: string | null | undefined;
  providerConfig: Record<string, unknown> | null | undefined;
  sellingPrice: number | null | undefined;
}): number | null {
  if (opts.sellingPrice == null || !Number.isFinite(Number(opts.sellingPrice))) return null;
  const strategy = (opts.strategy ?? "inherit") as PriceStrategy;
  return computeChannelPrice(strategy, Number(opts.sellingPrice), pricingConfigFromProviderConfig(opts.providerConfig));
}

function resolveProductName(productId: string | null, catalog: SyncEventsCatalog): { name: string | null; missing: boolean } {
  if (!productId) return { name: null, missing: false };
  const product = catalog.products[productId];
  if (!product) return { name: "Unknown/deleted product", missing: true };
  return { name: text(product.name) ?? "Unknown/deleted product", missing: !text(product.name) };
}

export interface TechnicalField {
  label: string;
  value: string;
}

export interface PresentedSyncJob {
  productName: string;
  missingEntity: boolean;
  fallbackIdLine: string | null;
  variantLine: string | null;
  skuLine: string | null;
  channelLine: string;
  workLabel: string;
  domainsLabel: string | null;
  sellableLine: string | null;
  priceLine: string | null;
  errorLine: string | null;
  technical: TechnicalField[];
  searchText: string;
}

export function presentSyncJob(
  job: {
    id: string;
    connector_id: string | null;
    job_type: string;
    status: string;
    payload: Record<string, unknown> | null;
    result?: Record<string, unknown> | null;
    error_message: string | null;
    entity_type?: string | null;
    entity_id?: string | null;
  },
  catalog: SyncEventsCatalog,
): PresentedSyncJob {
  const refs = extractJobRefs(job);
  const listing = refs.listingId ? catalog.listings[refs.listingId] : undefined;
  const variant = refs.variantId ? catalog.variants[refs.variantId] : undefined;
  const productId = refs.productId ?? listing?.product_id ?? variant?.product_id ?? null;
  const product = resolveProductName(productId, catalog);
  const connectorId = job.connector_id ?? listing?.connector_id ?? null;
  const connector = connectorId ? catalog.connectors[connectorId] : undefined;
  const includedCount = listing?.included_variant_count ?? null;
  const variantLabel = variant ? optionValuesLabel(variant.option_values) : null;
  const sku = text(variant?.sku) ?? null;

  let variantLine: string | null = variantLabel;
  if (!refs.variantId && includedCount && includedCount > 1) {
    variantLine = `${includedCount} variants`;
  } else if (!refs.variantId && includedCount === 1) {
    variantLine = "1 variant";
  } else if (!refs.variantId && !variant) {
    variantLine = "All variants";
  }

  const skuLine = sku ? `SKU ${sku}` : null;
  const workLabel = jobTypeDisplay(job.job_type, refs.domains);
  const domainsLabel = refs.domains.length ? refs.domains.join(", ") : null;

  let sellableLine: string | null = null;
  if (job.job_type === "inventory_publish" || refs.domains.includes("inventory")) {
    const fromPayload =
      finiteNumber(job.payload?.available) ??
      finiteNumber(job.payload?.available_qty) ??
      finiteNumber(job.payload?.sellable_qty);
    const fromLookup =
      refs.listingId && refs.variantId
        ? catalog.sellableByListingVariant[sellableLookupKey(refs.listingId, refs.variantId)]
        : undefined;
    const qty = fromPayload ?? (typeof fromLookup === "number" ? fromLookup : null);
    if (qty != null) sellableLine = `${qty} available`;
  }

  let priceLine: string | null = null;
  const isPriceWork = job.job_type === "price_publish" || refs.domains.includes("pricing");
  if (isPriceWork && listing) {
    if (variant) {
      const price = intendedChannelPrice({
        strategy: listing.price_strategy,
        providerConfig: listing.provider_config,
        sellingPrice: variant.selling_price,
      });
      if (price != null) priceLine = `Intended price ${price.toFixed(2)}`;
    } else {
      const prices = catalog.listingVariantPrices
        .filter((row) => row.listing_id === listing.id && row.included)
        .map((row) =>
          intendedChannelPrice({
            strategy: listing.price_strategy,
            providerConfig: listing.provider_config,
            sellingPrice: row.selling_price,
          }),
        )
        .filter((n): n is number => n != null);
      const unique = [...new Set(prices.map((n) => n.toFixed(2)))];
      if (unique.length === 1) priceLine = `Intended price ${unique[0]}`;
    }
  }

  const errorLine = text(job.error_message);

  const technical: TechnicalField[] = [
    { label: "Job ID", value: job.id },
    { label: "product_id", value: productId ?? "—" },
    { label: "variant_id", value: refs.variantId ?? "—" },
    { label: "listing_id", value: refs.listingId ?? "—" },
    { label: "ledger_id", value: refs.ledgerId ?? "—" },
    { label: "connector_id", value: connectorId ?? "—" },
    { label: "entity", value: [job.entity_type, job.entity_id].filter(Boolean).join(" ") || "—" },
  ];
  const safePayload = sanitizeTechnicalRecord(job.payload);
  if (safePayload) technical.push({ label: "payload", value: safePayload });

  const productName = product.name ?? "Unnamed product";
  const searchText = [
    productName,
    product.missing && productId ? productId : null,
    variantLine,
    sku,
    skuLine,
    channelDisplayLine(connector),
    workLabel,
    domainsLabel,
    sellableLine,
    priceLine,
    errorLine,
    job.job_type,
    connector?.name,
    connector?.provider,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    productName,
    missingEntity: product.missing,
    fallbackIdLine: product.missing && productId ? `Product ID: ${productId}` : null,
    variantLine,
    skuLine,
    channelLine: channelDisplayLine(connector),
    workLabel,
    domainsLabel,
    sellableLine,
    priceLine,
    errorLine,
    technical,
    searchText,
  };
}

export interface PresentedDomainEvent {
  eventType: string;
  headline: string | null;
  lines: string[];
  errorLine: string | null;
  technical: TechnicalField[];
  searchText: string;
}

export function presentDomainEvent(
  event: {
    id: number | string;
    event_type: string;
    aggregate_type: string | null;
    aggregate_id: string | null;
    payload: Record<string, unknown> | null;
    error?: string | null;
  },
  catalog: SyncEventsCatalog,
): PresentedDomainEvent {
  const refs = extractEventRefs(event);
  const payload = asRecord(event.payload) ?? {};
  const product = resolveProductName(refs.productId, catalog);
  const variant = refs.variantId ? catalog.variants[refs.variantId] : undefined;
  const order = refs.salesOrderId ? catalog.orders[refs.salesOrderId] : undefined;
  const connector = refs.connectorId ? catalog.connectors[refs.connectorId] : undefined;
  const lines: string[] = [];
  let headline: string | null = text(payload.name) ?? product.name;

  if (event.event_type === "ProductUpdated" || event.event_type === "ProductCreated") {
    headline = text(payload.name) ?? product.name;
  } else if (event.aggregate_type === "product") {
    headline = product.name ?? text(payload.name);
  }

  if (order?.order_number) lines.push(order.order_number);
  else if (text(payload.external_order_id)) {
    const ext = text(payload.external_order_id)!;
    lines.push(connector ? `${channelDisplayLine(connector)}-${ext}` : ext);
  }

  if (event.event_type === "RefundedUnfulfilledQuantityCancelled") {
    headline = order?.product_names[0] ?? product.name ?? headline;
    if (order && order.product_names.length > 1) headline = order.order_number ?? headline;
    const qty = finiteNumber(payload.qty_cancelled);
    if (qty != null) lines.push(`Qty cancelled: ${qty}`);
  }

  if (event.event_type === "InventoryChanged" || event.event_type === "InventoryAdjusted") {
    const sku = text(variant?.sku);
    if (sku) lines.push(`SKU ${sku}`);
    const available = finiteNumber(payload.available);
    if (available != null) lines.push(`${available} available`);
    const change = finiteNumber(payload.qty_change);
    if (change != null && event.event_type === "InventoryAdjusted") lines.push(`Qty change: ${change}`);
  }

  if (connector && event.event_type === "MarketplaceOrderImported") {
    lines.push(channelDisplayLine(connector));
  }

  const errorLine = text(event.error);
  const technical: TechnicalField[] = [
    { label: "Event ID", value: String(event.id) },
    { label: "aggregate", value: [event.aggregate_type, event.aggregate_id].filter(Boolean).join(" ") || "—" },
    { label: "product_id", value: refs.productId ?? "—" },
    { label: "variant_id", value: refs.variantId ?? "—" },
    { label: "sales_order_id", value: refs.salesOrderId ?? "—" },
  ];
  const safePayload = sanitizeTechnicalRecord(event.payload);
  if (safePayload) technical.push({ label: "payload", value: safePayload });

  const searchText = [event.event_type, headline, ...lines, connector?.name, order?.order_number, text(variant?.sku)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    eventType: event.event_type,
    headline,
    lines: [...new Set(lines.filter((l) => l && l !== headline))],
    errorLine,
    technical,
    searchText,
  };
}

export function matchesOperatorSearch(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystack.includes(q);
}

export function sanitizeTechnicalRecord(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (SENSITIVE_KEY.test(key)) continue;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = sanitizeTechnicalRecord(value as Record<string, unknown>);
      cleaned[key] = nested ? JSON.parse(nested) : {};
    } else {
      cleaned[key] = value;
    }
  }
  const keys = Object.keys(cleaned);
  if (!keys.length) return null;
  try {
    return JSON.stringify(cleaned);
  } catch {
    return null;
  }
}
